// The live event routes (bead hf-e4-events-needs-6r5.1).
//
// Where the pure surfaces meet the backend. Everything here is subscription and
// mutation plumbing; every decision about words, layout and which control to show
// belongs to `EventSurfaces.tsx`, which is why that file can be tested without a
// server and this one is thin enough to read in one sitting.
//
// ── THE BOUNDARY IS THE ONLY DOOR ───────────────────────────────────────────────
//
// Reads and writes go through `convex/_generated/api` and nothing else. Each function
// it reaches is a `makeCircleQuery` / `makeCircleMutation` that resolves the caller's
// Membership and capability before its handler runs, so there is no arrangement in
// which this component reaches circle data without that check having happened.
//
// ── WHY EVERY WRITE RE-READS ────────────────────────────────────────────────────
//
// A wrapped mutation returns `{ status: "applied" | "replayed" }` and the domain
// result inside it. Rather than patching local state from that, the surfaces re-read
// through the live query: Convex queries are reactive, so the record the organiser
// sees after a save is the record the server holds, not an optimistic guess that can
// disagree with it. For an edit that made somebody recheck their commitment, a
// disagreement would be somebody's obligation shown wrongly.

import { StatusMessage } from "@herefor/design-system";
import { ERROR_MESSAGES, EVENT_SURFACE_MESSAGES as COPY } from "@herefor/product-language";
import { timeZone, type TimeZoneId } from "@herefor/domain-kernel";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import { eventMutations, eventQueries, lifecycleProblem } from "../../convex/events";
import { useConnectivity } from "../../offline/offline";
import {
  EMPTY_EVENT_FORM,
  classifyPendingEdit,
  toCreateInput,
  toLocalInputValue,
  toUpdateInput,
  type EventDetailView,
  type EventFormValues,
  type FieldProblem,
} from "./eventData";
import { EventDetail, EventForm, EventHistory, type EventAffordances } from "./EventSurfaces";

export interface EventRouteContext {
  readonly circleId: string;
  readonly timeZone: string;
  /**
   * What the SERVER says this person may do. Never inferred here from a role.
   *
   * Supplied by the caller because the plan read model is where capability-derived
   * affordances belong — one place, decided server-side, for every surface.
   */
  readonly affordances: EventAffordances;
  readonly onDone: () => void;
}

/* ================================================================ the create === */

export function NewEventRoute({
  circleId,
  timeZone: zoneName,
  affordances,
  onDone,
}: EventRouteContext) {
  const zone = timeZone(zoneName);
  const create = useMutation(eventMutations.create);
  const [values, setValues] = useState<EventFormValues>(EMPTY_EVENT_FORM);
  const [problems, setProblems] = useState<readonly FieldProblem[]>([]);
  const [busy, setBusy] = useState(false);
  const { actionsPaused } = useConnectivity();

  // Fail closed. Somebody who cannot create is shown the same thing as somebody who
  // asked for a page that is not there — never a form that will reject them.
  if (!affordances.canEdit) return <Unavailable />;

  const submit = () => {
    if (actionsPaused) return;
    const validated = toCreateInput(values, circleId, zone);
    if (!validated.ok) {
      setProblems(validated.problems);
      return;
    }

    setProblems([]);
    setBusy(true);
    void create(validated.input as never)
      .then(onDone)
      .catch(() => {
        setProblems([{ field: "form", message: ERROR_MESSAGES.serviceUnavailable.body }]);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <EventForm
      values={values}
      onChange={setValues}
      onSubmit={submit}
      problems={problems}
      submitLabel={COPY.create}
      heading={COPY.createTitle}
      headingLevel={1}
      busy={busy || actionsPaused}
    />
  );
}

/* ================================================================== the edit === */

export function EditEventRoute({
  circleId,
  timeZone: zoneName,
  affordances,
  eventId,
  onDone,
}: EventRouteContext & { readonly eventId: string }) {
  const zone = timeZone(zoneName);
  const found = useQuery(eventQueries.get, { circleId, eventId });
  const update = useMutation(eventMutations.update);
  const [values, setValues] = useState<EventFormValues | null>(null);
  const [problems, setProblems] = useState<readonly FieldProblem[]>([]);
  const [busy, setBusy] = useState(false);
  const { actionsPaused } = useConnectivity();

  if (!affordances.canEdit) return <Unavailable />;
  if (found === undefined) return null;
  if (!found.ok) return <Unavailable />;

  const original = toDetailView(found.value, zone);
  const current = values ?? formFrom(found.value, zone);

  const submit = () => {
    if (actionsPaused) return;
    const validated = toUpdateInput(current, original, circleId, zone);
    if (!validated.ok) {
      setProblems(validated.problems);
      return;
    }

    setProblems([]);
    setBusy(true);
    void update(validated.input as never)
      .then(onDone)
      .catch(() => {
        setProblems([{ field: "form", message: ERROR_MESSAGES.serviceUnavailable.body }]);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <EventForm
      values={current}
      onChange={setValues}
      onSubmit={submit}
      problems={problems}
      submitLabel={COPY.save}
      heading={COPY.editTitle}
      headingLevel={1}
      // The kernel's own verdict, live, so the warning arrives while the organiser
      // can still change their mind (§10.8).
      pendingChange={classifyPendingEdit(current, original, zone)}
      busy={busy || actionsPaused}
    />
  );
}

/* ================================================================ the detail === */

export function EventRoute({
  circleId,
  timeZone: zoneName,
  affordances,
  eventId,
  committedPeopleCount,
  linkedNeedCount,
  onEdit,
  onDone,
}: EventRouteContext & {
  readonly eventId: string;
  readonly committedPeopleCount: number;
  readonly linkedNeedCount: number;
  readonly onEdit: () => void;
}) {
  const zone = timeZone(zoneName);
  const found = useQuery(eventQueries.get, { circleId, eventId });
  const history = useQuery(eventQueries.revisions, { circleId, eventId });
  const publish = useMutation(eventMutations.publish);
  const complete = useMutation(eventMutations.complete);
  const cancel = useMutation(eventMutations.cancel);
  const deleteDraft = useMutation(eventMutations.deleteDraft);
  const [busy, setBusy] = useState(false);
  const [actionProblem, setActionProblem] = useState<string | null>(null);
  const { actionsPaused } = useConnectivity();

  if (found === undefined) return null;
  if (!found.ok) return <Unavailable />;

  const view = toDetailView(found.value, zone);
  const args = { circleId, eventId, expectedRevision: view.revision };
  const run = (call: () => Promise<unknown>, after: () => void) => {
    if (actionsPaused) return;
    setActionProblem(null);
    setBusy(true);
    void call()
      .then((outcome) => {
        const problem = lifecycleProblem(outcome);
        if (problem === null) after();
        else setActionProblem(problem);
      })
      .catch(() => {
        setActionProblem(ERROR_MESSAGES.serviceUnavailable.body);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <>
      <EventDetail
        event={view}
        headingLevel={1}
        affordances={affordances}
        committedPeopleCount={committedPeopleCount}
        linkedNeedCount={linkedNeedCount}
        onEdit={onEdit}
        onPublish={() => {
          run(
            () => publish(args),
            () => undefined,
          );
        }}
        onMarkDone={() => {
          run(
            () => complete(args),
            () => undefined,
          );
        }}
        onCancel={() => {
          run(
            () => cancel(args),
            () => undefined,
          );
        }}
        // A deleted draft has nothing left to look at, so this one navigates away.
        onDeleteDraft={() => {
          run(() => deleteDraft(args), onDone);
        }}
        busy={busy || actionsPaused}
        actionProblem={actionProblem}
      />
      <EventHistory
        headingLevel={2}
        entries={
          history?.ok !== true
            ? []
            : history.value.map((entry, index, entries) => ({
                revision: entry.revision,
                changedAtLabel: toLocalInputValue(entry.changedAt as never, zone).replace("T", " "),
                changes: changesIn(entry, entries[index - 1]),
              }))
        }
      />
    </>
  );
}

/* ------------------------------------------------------------------ plumbing --- */

function Unavailable() {
  return (
    <section>
      <h1>{ERROR_MESSAGES.itemUnavailable.title}</h1>
      <StatusMessage tone="info">{ERROR_MESSAGES.itemUnavailable.body}</StatusMessage>
    </section>
  );
}

interface ServerEvent {
  readonly eventId: string;
  readonly title: string;
  readonly shareTitle: string;
  readonly category: string;
  readonly startsAt?: number;
  readonly endsAt?: number;
  readonly locationLabel?: string;
  readonly locationPrivate?: string;
  readonly practicalNote?: string;
  readonly state: "draft" | "scheduled" | "completed" | "cancelled";
  readonly revision: number;
}

function toDetailView(event: ServerEvent, zone: TimeZoneId): EventDetailView {
  const window =
    event.startsAt === undefined
      ? null
      : `${toLocalInputValue(event.startsAt as never, zone).replace("T", " ")}${
          event.endsAt === undefined
            ? ""
            : ` – ${toLocalInputValue(event.endsAt as never, zone).slice(11)}`
        }`;

  return {
    eventId: event.eventId,
    title: event.title,
    shareTitle: event.shareTitle,
    category: event.category,
    windowLabel: window,
    startsAt: event.startsAt ?? null,
    endsAt: event.endsAt ?? null,
    locationLabel: event.locationLabel ?? null,
    locationPrivate: event.locationPrivate ?? null,
    practicalNote: event.practicalNote ?? null,
    state: event.state,
    revision: event.revision,
    // Past is a display treatment, decided against the clock at render time; the
    // record itself never stores it (plan §10.2).
    isPast: event.endsAt !== undefined && event.endsAt < Date.now(),
  };
}

function formFrom(event: ServerEvent, zone: TimeZoneId): EventFormValues {
  return {
    title: event.title,
    shareTitle: event.shareTitle,
    category: event.category,
    startsAtLocal:
      event.startsAt === undefined ? "" : toLocalInputValue(event.startsAt as never, zone),
    endsAtLocal: event.endsAt === undefined ? "" : toLocalInputValue(event.endsAt as never, zone),
    locationLabel: event.locationLabel ?? "",
    locationPrivate: event.locationPrivate ?? "",
    practicalNote: event.practicalNote ?? "",
  };
}

/** What changed between two material snapshots — codes only; never the address. */
export function changesIn(
  entry: {
    readonly revision: number;
    readonly startsAt?: number;
    readonly endsAt?: number;
    readonly locationFingerprint?: string;
  },
  previous?: {
    readonly startsAt?: number;
    readonly endsAt?: number;
    readonly locationFingerprint?: string;
  },
): readonly ("time" | "location" | "created")[] {
  if (entry.revision <= 1) return ["created"];
  const changes: ("time" | "location")[] = [];
  if (entry.startsAt !== previous?.startsAt || entry.endsAt !== previous?.endsAt) {
    changes.push("time");
  }
  if (entry.locationFingerprint !== previous?.locationFingerprint) changes.push("location");
  return changes;
}
